import React, { useState, useEffect } from 'react';
import './StatusHealth.css';
import { auth } from './firebase';
import { useModels } from './contexts/ModelContext';
import { getEndpoint } from './chatModels';
import { buildApiUrl } from './config/env';

const StatusHealth = () => {
  const { models: catalog } = useModels();
  const [models, setModels] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [providerSummaryExpanded, setProviderSummaryExpanded] = useState(false);

  const API_BASE_URL = buildApiUrl('').replace(/\/$/, '');

  // Initialize all models with undecided state, sourced from Firestore catalog
  useEffect(() => {
    if (!catalog || catalog.length === 0) return;
    const prepared = catalog.map((model) => ({
      modelString: model.modelString,
      modelName: model.name,
      provider: model.displayProvider || model.provider,
      endpoint: getEndpoint(model, true) || `${API_BASE_URL}/openai`,
      status: 'undecided',
      error: null,
      responseTime: null
    }));
    setModels(prepared);
  }, [catalog]);

  const runStreamingHealthcheck = async () => {
    setIsStreaming(true);
    setError(null);
    setSummary(null);
    
    // Reset all models to loading state
    setModels(prev => prev.map(model => ({
      ...model,
      status: 'loading',
      error: null,
      responseTime: null
    })));

    try {
      if (!auth.currentUser) throw new Error('Sign in to run model checks.');
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/healthcheck/stream/full`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'result') {
                // Update specific model with result
                setModels(prev => prev.map(model => 
                  model.modelString === data.modelString 
                    ? {
                        ...model,
                        status: data.success ? 'success' : 'error',
                        error: data.error || null,
                        responseTime: data.responseTime || null
                      }
                    : model
                ));
              } else if (data.type === 'summary') {
                setSummary(data);
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (err) {
      setError(err.message);
      // Reset all models to error state
      setModels(prev => prev.map(model => ({
        ...model,
        status: 'error',
        error: err.message
      })));
    } finally {
      setIsStreaming(false);
    }
  };

  const getStatusEmoji = (status) => {
    switch (status) {
      case 'success': return '🟢';
      case 'error': return '🔴';
      case 'loading': return <div className="inline-spinner"></div>;
      default: return '⚪';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success': return 'Healthy';
      case 'error': return 'Unhealthy';
      case 'loading': return 'Testing...';
      default: return 'Pending';
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'success': return 'status-healthy';
      case 'error': return 'status-unhealthy';
      case 'loading': return 'status-loading';
      default: return 'status-undecided';
    }
  };

  const renderSummaryStats = () => {
    if (!summary) return null;

    const successfulTests = models.filter(m => m.status === 'success').length;
    const failedTests = models.filter(m => m.status === 'error').length;
    const totalTests = models.length;
    const healthScore = totalTests > 0 ? Math.round((successfulTests / totalTests) * 100) : 0;
    const overallStatus = healthScore >= 80 ? 'healthy' : healthScore >= 50 ? 'degraded' : 'unhealthy';

    return (
      <div className="summary-stats">
        <div className="stat">
          <span className="stat-label">Overall Status:</span>
          <span className={`stat-value ${getStatusClass(overallStatus === 'healthy')}`}>
            {getStatusEmoji(overallStatus === 'healthy')} {overallStatus}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Health Score:</span>
          <span className="stat-value">{healthScore}%</span>
        </div>
        <div className="stat">
          <span className="stat-label">Successful Tests:</span>
          <span className="stat-value success">{successfulTests}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Failed Tests:</span>
          <span className="stat-value error">{failedTests}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Models:</span>
          <span className="stat-value">{totalTests}</span>
        </div>
      </div>
    );
  };

  const renderProviderSummary = () => {
    const providerStats = {};
    
    models.filter(model => model && model.provider).forEach(model => {
      if (!providerStats[model.provider]) {
        providerStats[model.provider] = { total: 0, successful: 0, failed: 0, loading: 0 };
      }
      providerStats[model.provider].total++;
      
      if (model.status === 'success') {
        providerStats[model.provider].successful++;
      } else if (model.status === 'error') {
        providerStats[model.provider].failed++;
      } else if (model.status === 'loading') {
        providerStats[model.provider].loading++;
      }
    });

    return (
      <div className="provider-summary">
        <div className="provider-summary-header" onClick={() => setProviderSummaryExpanded(!providerSummaryExpanded)}>
          <h4>Provider Summary</h4>
          <div className={`collapse-icon ${providerSummaryExpanded ? 'expanded' : 'collapsed'}`}>
            {providerSummaryExpanded ? '▼' : '▶'}
          </div>
        </div>
        <div className={`provider-summary-content ${providerSummaryExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="provider-grid">
            {Object.entries(providerStats).map(([provider, stats]) => (
              <div key={provider} className="provider-card">
                <h5>{provider}</h5>
                <div className="provider-stats">
                  <div>Total: {stats.total}</div>
                  <div className="success">Success: {stats.successful}</div>
                  <div className="error">Failed: {stats.failed}</div>
                  {stats.loading > 0 && (
                    <div className="loading">Testing: {stats.loading}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="status-health-container">
      <div className="status-health-header">
        <h1>🩺 System Health Check</h1>
        <p>Monitor the health of all AI model endpoints</p>
      </div>

      {error && (
        <div className="error-message">
          <h3>❌ Error</h3>
          <p>{error}</p>
        </div>
      )}

      {renderSummaryStats()}
      {renderProviderSummary()}

      <div className="results-section">
        <div className="results-header">
          <h3>Model Status</h3>
          <button 
            onClick={runStreamingHealthcheck}
            disabled={isStreaming}
            className="health-button streaming-full"
          >
            {isStreaming ? '🔄 Running Full Health Check...' : '📡 Run Full Health Check'}
          </button>
        </div>
        <div className="results-table">
          <table>
            <thead>
              <tr>
                <th>Model</th>
                <th>Provider</th>
                <th>Endpoint</th>
                <th>Status</th>
                <th>Response Time</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model, index) => (
                <tr key={index}>
                  <td>
                    <div className="model-info">
                      <div className="model-name">{model.modelName}</div>
                      <div className="model-string">{model.modelString}</div>
                    </div>
                  </td>
                  <td>{model.provider}</td>
                  <td className="endpoint">{model.endpoint}</td>
                  <td>
                    <span className={`status ${getStatusClass(model.status)}`}>
                      {getStatusEmoji(model.status)} {getStatusText(model.status)}
                    </span>
                  </td>
                  <td>{model.responseTime || 'N/A'}</td>
                  <td className="error-cell">
                    {model.error && (
                      <div className="error-tooltip">
                        <span className="error-dot">•</span>
                        <div className="error-content">{model.error}</div>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
};

export default StatusHealth; 
